import { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactModal from "react-modal";
import { Info, X } from "lucide-react";
import { toast } from "sonner";

import { InfoButton } from "@/components/common/buttons";
import { Spinner } from "@/components/common/svgs";
import { TranslatedText } from "@/components/common/text";
import { useLoadFinderStore } from "@/state/loadFinderStore";
import { useDebounce } from "@/utils/useDebounce";
import { buildStrategyPills } from "./strategyPills";

const EXTRACT_DEBOUNCE_MS = 1500;

const FieldHeader = ({ label, tooltip, onReset, showReset }) => (
    <div className="flex items-center gap-2 px-1">
        <span className="text-[12px] font-medium uppercase tracking-wider text-cm-p-text-secondary whitespace-nowrap">
            <TranslatedText>{label}</TranslatedText>
        </span>
        {tooltip && <InfoButton text={tooltip} />}
        <div className="flex-1 text-right">
            {showReset && (
                <button type="button" onClick={onReset} className="text-[12px] font-medium text-cm-p-on-container-red hover:underline">
                    <TranslatedText>Reset</TranslatedText>
                </button>
            )}
        </div>
    </div>
);

const StrategyTextarea = ({ value, onChange, placeholder, filled }) => {
    const ref = useRef(null);
    // Auto-grow with content. Reset to auto first so the scrollHeight reflects the
    // current content, then expand to fit.
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
    }, [value]);
    return (
        <textarea
            ref={ref}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            rows={1}
            className={`w-full resize-none overflow-hidden border border-cm-p-outline rounded-[6px] p-3 text-[16px] text-cm-p-text-primary placeholder:text-cm-p-text-secondary focus:outline-none focus:border-cm-p-accent-300 ${
                filled ? "bg-cm-p-surface-container" : "bg-cm-p-surface"
            }`}
        />
    );
};

// Mirrors StrategyPillRow's source-based styling so global vs truck-specific
// parameters read the same in the modal preview as they do in LoadFinder.
const ParameterChip = ({ source, children }) => {
    const sourceClass = source === "truck" ? "bg-cm-p-surface-container" : "bg-cm-p-surface border-[1.5px] border-cm-p-outline";
    return (
        <span
            className={`flex items-center justify-center gap-1 px-3 py-[6px] rounded-[58px] whitespace-nowrap text-[14px] font-medium text-cm-p-text-primary ${sourceClass}`}
        >
            {children}
        </span>
    );
};

const ParametersBody = ({ truckPreview, globalPreview, loading, error }) => {
    if (loading) {
        return (
            <div className="flex items-center gap-2 text-[14px] text-cm-p-text-secondary">
                <Spinner className="w-4 h-4 text-cm-p-accent" />
                <TranslatedText>Extracting parameters…</TranslatedText>
            </div>
        );
    }
    if (error) {
        return (
            <div className="flex items-start gap-2 text-[14px] text-cm-p-on-container-red">
                <Info className="w-4 h-4 mt-[2px] shrink-0" />
                <span>{error}</span>
            </div>
        );
    }
    const pills = buildStrategyPills(truckPreview, globalPreview);
    if (pills.length === 0) {
        return (
            <p className="text-[14px] text-cm-p-text-secondary px-1">
                <TranslatedText>
                    Once you fill in the details, the strategy parameters will appear right here. To change or remove a parameter, edit the
                    matching strategy field.
                </TranslatedText>
            </p>
        );
    }
    return (
        <div className="flex flex-wrap items-start gap-2">
            {pills.map((pill, idx) => (
                <ParameterChip key={`${pill.label}-${idx}`} source={pill.source}>
                    <TranslatedText>{pill.label}</TranslatedText>
                </ParameterChip>
            ))}
        </div>
    );
};

export const AdjustStrategyModal = ({ isOpen, selectedTruck, truckId, onClose }) => {
    const { strategy, fetchGlobalStrategyText, fetchTruckStrategyText, extractStrategyPreview, clearExtractPreview, saveStrategy } =
        useLoadFinderStore();

    const [globalText, setGlobalText] = useState("");
    const [truckText, setTruckText] = useState("");
    const initialGlobalRef = useRef("");
    const initialTruckRef = useRef("");

    useEffect(() => {
        if (!isOpen) return;
        clearExtractPreview();
        fetchGlobalStrategyText();
        if (truckId != null) fetchTruckStrategyText(truckId);
    }, [isOpen, truckId]);

    useEffect(() => {
        if (!isOpen) return;
        const seededGlobal = strategy.globalText ?? "";
        const seededTruck = (truckId != null && strategy.truckText[truckId]) || "";
        initialGlobalRef.current = seededGlobal;
        initialTruckRef.current = seededTruck;
        setGlobalText(seededGlobal);
        setTruckText(seededTruck);
    }, [isOpen, strategy.globalText, strategy.truckText, truckId]);

    const debouncedGlobal = useDebounce(globalText, EXTRACT_DEBOUNCE_MS);
    const debouncedTruck = useDebounce(truckText, EXTRACT_DEBOUNCE_MS);
    useEffect(() => {
        if (!isOpen) return;
        extractStrategyPreview({ globalText: debouncedGlobal, truckText: debouncedTruck, truckId });
    }, [isOpen, debouncedGlobal, debouncedTruck, truckId]);

    const handleClose = () => {
        clearExtractPreview();
        onClose();
    };

    const globalChanged = globalText !== initialGlobalRef.current;
    const truckChanged = truckText !== initialTruckRef.current;
    const canSave = (globalChanged || truckChanged) && !strategy.saveState.loading;

    const handleSave = async () => {
        if (!canSave) return;
        await saveStrategy({
            truckId,
            globalText: globalChanged ? globalText : null,
            truckText: truckChanged ? truckText : null,
        });
        const error = useLoadFinderStore.getState().strategy.saveState.error;
        if (error) return;
        toast.success("Strategy saved", { className: "cm-toast-success", toasterId: "top-right" });
        handleClose();
    };

    const titleName = selectedTruck?.truckName ?? "";

    return (
        <ReactModal
            isOpen={isOpen}
            onRequestClose={handleClose}
            shouldCloseOnOverlayClick={false}
            ariaHideApp={false}
            overlayClassName="ReactModal__Overlay fixed inset-0 bg-black/70 z-modal flex items-center justify-center"
            className="fixed inset-0 flex items-center justify-center pointer-events-none"
        >
            <div className="bg-cm-p-surface border border-cm-p-outline rounded-[12px] w-[1000px] max-w-[calc(100vw-32px)] flex flex-col pointer-events-auto shadow-xl overflow-clip">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-cm-p-outline pl-5 pr-3 py-3">
                    <span className="text-[18px] font-medium text-cm-p-text-primary tracking-[-0.18px] whitespace-nowrap">
                        {titleName ? `${titleName} ` : ""}
                        <TranslatedText>Strategy</TranslatedText>
                    </span>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="flex items-center justify-center w-10 h-10 rounded hover:bg-cm-p-surface-hover text-cm-p-text-primary"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex flex-col gap-8 px-4 pt-5 pb-8">
                    {/* Textarea group */}
                    <div className="flex flex-col gap-5">
                        <div className="flex flex-col gap-1">
                            <FieldHeader
                                label="Shared strategy across all drivers"
                                tooltip="With a shared strategy, you can define the load characteristics for all your trucks. This strategy is common across all trucks, so changing it for one will apply to all other trucks."
                                onReset={() => setGlobalText("")}
                                showReset={globalText !== ""}
                            />
                            <StrategyTextarea
                                value={globalText}
                                onChange={setGlobalText}
                                placeholder="e.g. Prefers 1,000+ mi flatbed loads from IL/WI/MN going West or South. Min rate $1.40/mi."
                            />
                        </div>

                        <div className="flex flex-col gap-1">
                            <FieldHeader
                                label="Driver specific strategy"
                                tooltip="With a driver specific strategy, you can define the load characteristics specific for this truck that is going to be combined with shared strategy."
                                onReset={() => setTruckText("")}
                                showReset={truckText !== ""}
                            />
                            <StrategyTextarea
                                value={truckText}
                                onChange={setTruckText}
                                placeholder="e.g. Prefers 1,000+ mi flatbed loads from IL/WI/MN going West or South. Min rate $1.40/mi."
                                filled
                            />
                        </div>
                    </div>

                    {/* Strategy parameters */}
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2 px-1">
                            <span className="text-[12px] font-medium uppercase tracking-wider text-cm-p-text-secondary whitespace-nowrap">
                                <TranslatedText>Strategy parameters</TranslatedText>
                            </span>
                            <InfoButton text="These are the filter values extracted from your strategy text." />
                        </div>
                        <ParametersBody
                            truckPreview={strategy.extractPreviewTruck}
                            globalPreview={strategy.extractPreviewGlobal}
                            loading={strategy.extractState.loading}
                            error={strategy.extractState.error}
                        />
                    </div>

                    {strategy.saveState.error && (
                        <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-cm-p-container-red text-cm-p-on-container-red text-[13px]">
                            <Info className="w-4 h-4 mt-[2px] shrink-0" />
                            <span>{strategy.saveState.error}</span>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-[10px] border-t border-cm-p-outline p-4">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="w-[120px] h-[44px] flex items-center justify-center rounded-[6px] bg-cm-p-surface-container text-cm-p-text-primary text-[14px] font-medium"
                    >
                        <TranslatedText>Cancel</TranslatedText>
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={!canSave}
                        className={`w-[120px] h-[44px] flex items-center justify-center rounded-[6px] text-[14px] font-medium ${
                            canSave
                                ? "bg-cm-p-accent-500 text-cm-p-neutral-dark-500"
                                : "bg-cm-p-accent-50 text-cm-p-text-secondary cursor-not-allowed"
                        }`}
                    >
                        <TranslatedText>Save</TranslatedText>
                    </button>
                </div>
            </div>
        </ReactModal>
    );
};
